// Aba Segurança do painel Aplicativo: a linha do tempo da porta do app — senhas
// que saíram, recusas (com o motivo em português) e o que o escritório fez.
import { useState } from 'react'
import { FiSend, FiLock, FiKey, FiAlertTriangle } from 'react-icons/fi'
import { API_APP, useApp, Carregando, Falha, Cabeca, Chip, Segmento, num, quando, titulo } from './comum'
import { FotoPessoa, useFotoDe } from '../foto/FotoPessoa'

/** O log é denso (centenas de linhas): a foto entra só quando a pessoa tem uma. */
function FotoSeHouver({ codigo, nome, tamanho, expandir = true, className }: { codigo: string; nome: string | null; tamanho: number; expandir?: boolean; className?: string }) {
  const url = useFotoDe(codigo)
  return url ? <FotoPessoa foto={url} nome={titulo(nome) || codigo} tamanho={tamanho} expandir={expandir} className={className} /> : null
}

type Ev = {
  tipo: 'senha' | 'recusa' | 'acao'; em: string; codigo: string; nome: string | null; rotulo: string
  motivo?: string; por?: string | null; final?: string | null; destino?: string; porta?: string; bloqueio?: boolean; acao?: string
}
type Dados = {
  resumo: { senhas: number; aprovacoes: number; esqueceu: number; escritorio: number; recusas: number; bloqueios: number; acoes: number }
  bloqueadas_agora: { codigo: string; nome: string | null; ate: string; falhas: number }[]
  por_dia: { dia: string; senha: number; recusa: number; acao: number }[]
  eventos: Ev[]
}

export function Seguranca({ abrirPessoa }: { abrirPessoa: (codigo: string) => void }) {
  const [dias, setDias] = useState<'1' | '7' | '30'>('7')
  const [tipo, setTipo] = useState<'' | Ev['tipo']>('')
  const { dados, erro, carregando, recarregar } = useApp<Dados>(`${API_APP}/seguranca?dias=${dias}`)
  if (!dados) return erro ? <Falha erro={erro} tentar={recarregar} /> : <Carregando />
  const r = dados.resumo
  // A mesma pessoa batendo na mesma porta em sequência (7 tentativas em 2 min) vira UMA
  // linha com a contagem: a lista mostra o que aconteceu, não cada toque no botão.
  const eventos: (Ev & { vezes: number; ate: string })[] = []
  for (const e of dados.eventos.filter(x => !tipo || x.tipo === tipo)) {
    const ult = eventos[eventos.length - 1]
    if (ult && ult.codigo === e.codigo && ult.tipo === e.tipo && ult.rotulo === e.rotulo && Math.abs(new Date(ult.em).getTime() - new Date(e.em).getTime()) < 3600000) {
      ult.vezes += 1; ult.ate = e.em
    } else eventos.push({ ...e, vezes: 1, ate: e.em })
  }
  const max = Math.max(1, ...dados.por_dia.map(d => d.senha + d.recusa + d.acao))

  return (
    <div className="h-full min-h-0 flex flex-col gap-3 p-4 sm:p-5">
      <Cabeca
        titulo="Segurança — a porta do app"
        sub="Senhas enviadas, tentativas recusadas e ações do escritório. Sem senha, sem CPF: só código, motivo e quem fez."
        carregando={carregando} recarregar={recarregar}
        direita={<Segmento rotulo="Período" valor={dias} aoMudar={setDias} opcoes={[['1', 'Hoje'], ['7', '7 dias'], ['30', '30 dias']]} />}
        chips={<>
          <Chip ativo={tipo === ''} onClick={() => setTipo('')}>Tudo · {num(r.senhas + r.recusas + r.acoes)}</Chip>
          <Chip tom="info" ativo={tipo === 'senha'} onClick={() => setTipo('senha')} title={`${r.aprovacoes} aprovações · ${r.esqueceu} esqueceram · ${r.escritorio} pelo escritório`}>
            Senhas enviadas · {num(r.senhas)}
          </Chip>
          <Chip tom="alerta" ativo={tipo === 'recusa'} onClick={() => setTipo('recusa')}>Recusas · {num(r.recusas)}</Chip>
          <Chip ativo={tipo === 'acao'} onClick={() => setTipo('acao')}>Ações do escritório · {num(r.acoes)}</Chip>
          {r.bloqueios > 0 && <Chip tom="ruim">{num(r.bloqueios)} bloqueios por senha errada</Chip>}
        </>}
      />

      {dados.bloqueadas_agora.length > 0 && (
        <div className="shrink-0 rounded-2xl bg-red-500/10 px-3.5 py-2.5 text-[12px] text-red-800 dark:text-red-300 flex items-start gap-2 flex-wrap">
          <FiAlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span className="font-semibold">Bloqueadas agora:</span>
          {dados.bloqueadas_agora.map(b => (
            <button key={b.codigo} onClick={() => abrirPessoa(b.codigo)} className="underline inline-flex items-center gap-1.5">
              <FotoSeHouver codigo={b.codigo} nome={b.nome} tamanho={20} expandir={false} />
              {titulo(b.nome) || b.codigo} (até {quando(b.ate)})
            </button>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 flex-1 min-h-0">
        <section className="neu-card rounded-2xl flex flex-col min-h-0 lg:col-span-2">
          <div className="flex-1 min-h-0 overflow-y-auto scrollbar-hidden">
            {eventos.length === 0 ? <p className="text-[12.5px] text-muted-foreground text-center py-10">Nada neste período.</p> : (
              <ul className="divide-y divide-border/60">
                {eventos.map((e, i) => {
                  const Icone = e.tipo === 'senha' ? FiSend : e.tipo === 'recusa' ? FiLock : FiKey
                  const cor = e.tipo === 'senha' ? 'text-sky-600' : e.tipo === 'recusa' ? (e.bloqueio ? 'text-red-600' : 'text-amber-600') : 'text-foreground'
                  return (
                    <li key={i} className="px-3.5 py-2 flex items-start gap-2.5">
                      {/* O ícone diz O QUE aconteceu (senha, recusa, ação); a foto, com QUEM. */}
                      <Icone className={`w-4 h-4 mt-0.5 shrink-0 ${cor}`} />
                      <FotoSeHouver codigo={e.codigo} nome={e.nome} tamanho={24} />
                      <div className="min-w-0 flex-1">
                        <p className="text-[12.5px]">
                          <button onClick={() => abrirPessoa(e.codigo)} className="font-semibold hover:underline" title="Abrir a pessoa (se ela tiver conta)">{titulo(e.nome) || e.codigo}</button>
                          {e.nome && <span className="text-muted-foreground tabular-nums"> · {e.codigo}</span>}
                          {e.vezes > 1 && <span className="ml-1.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300">{e.vezes}×</span>}
                        </p>
                        <p className="text-[11.5px] text-muted-foreground break-words">
                          {e.tipo === 'recusa' && (e.porta === 'entrar' ? 'Tentou entrar — ' : 'Pediu senha — ')}
                          {e.rotulo}
                          {e.final ? ` · final ••${e.final}` : ''}{e.destino === 'informado' ? ' · número INFORMADO' : ''}
                          {e.por ? ` · ${e.por}` : e.tipo === 'senha' && e.motivo === 'recuperacao' ? ' · ela mesma, no app' : ''}
                          {e.tipo === 'acao' && e.motivo ? ` — “${e.motivo}”` : ''}
                        </p>
                      </div>
                      <span className="text-[11px] text-muted-foreground tabular-nums shrink-0" title={e.vezes > 1 ? `de ${quando(e.ate)} a ${quando(e.em)}` : undefined}>{quando(e.em)}</span>
                    </li>
                  )
                })}
              </ul>
            )}
          </div>
        </section>

        <section className="neu-card rounded-2xl p-4 hidden lg:flex flex-col gap-2 min-h-0 overflow-y-auto scrollbar-hidden">
          <h3 className="text-[13px] font-semibold">Por dia</h3>
          {dados.por_dia.length === 0 ? <p className="text-[12px] text-muted-foreground">Nada neste período.</p> : [...dados.por_dia].reverse().map(d => {
            const total = d.senha + d.recusa + d.acao
            return (
              <div key={d.dia} className="text-[11.5px]">
                <div className="flex justify-between"><span className="tabular-nums">{d.dia.slice(8, 10)}/{d.dia.slice(5, 7)}</span><span className="text-muted-foreground tabular-nums">{d.senha} senhas · {d.recusa} recusas{d.acao ? ` · ${d.acao} ações` : ''}</span></div>
                <div className="h-1.5 rounded-full bg-foreground/[0.06] overflow-hidden flex mt-0.5" style={{ width: `${(total / max) * 100}%`, minWidth: 4 }}>
                  <span className="h-full bg-sky-500/80" style={{ width: `${(d.senha / total) * 100}%` }} />
                  <span className="h-full bg-amber-500/80" style={{ width: `${(d.recusa / total) * 100}%` }} />
                  <span className="h-full bg-foreground/50" style={{ width: `${(d.acao / total) * 100}%` }} />
                </div>
              </div>
            )
          })}
          <p className="text-[10.5px] text-muted-foreground mt-auto">Azul: senhas · âmbar: recusas · cinza: ações do escritório</p>
        </section>
      </div>
    </div>
  )
}
